import { useState, useEffect, useRef } from "react";
import { motion, useDragControls, useMotionValue } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import useTickets from "../../api/ticket";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Plus,
  MessageSquare,
  Clock,
  CheckCircle2,
  AlertCircle,
  Filter,
  Check,
  Clock3,
  HelpCircle,
  X,
} from "lucide-react";

const getStatusColor = (status, isDark) => {
  switch (status) {
    case "open":
      return isDark
        ? "bg-blue-500/15 text-blue-400"
        : "bg-blue-50 text-blue-700";
    case "in progress":
      return isDark
        ? "bg-amber-500/15 text-amber-400"
        : "bg-amber-50 text-amber-700";
    case "closed":
      return isDark
        ? "bg-emerald-500/15 text-emerald-400"
        : "bg-emerald-50 text-emerald-700";
    default:
      return isDark
        ? "bg-white/[0.06] text-gray-400"
        : "bg-gray-100 text-gray-600";
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case "open":
      return <AlertCircle className="h-3 w-3" />;
    case "in progress":
      return <Clock className="h-3 w-3" />;
    case "closed":
      return <CheckCircle2 className="h-3 w-3" />;
    default:
      return null;
  }
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const TicketsManager = () => {
  const { company } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { getCompanyTickets, createTicket } = useTickets();
  const location = useLocation();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // New ticket form
  const [newTicketTitle, setNewTicketTitle] = useState("");
  const [newTicketDescription, setNewTicketDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false,
  );
  const sheetRef = useRef(null);
  const sheetContentRef = useRef(null);
  const sheetDragControls = useDragControls();
  const sheetY = useMotionValue(0);
  const isSheetScrolledToTop = useRef(true);
  const sheetTouchStartY = useRef(null);
  const sheetDragging = useRef(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Attach native touch listeners for pull-down-to-dismiss from anywhere in the content
  useEffect(() => {
    isSheetScrolledToTop.current = true;
    sheetTouchStartY.current = null;
    sheetDragging.current = false;

    const el = sheetContentRef.current;
    if (!el || !isMobile) return;

    const onScroll = () => {
      isSheetScrolledToTop.current = el.scrollTop <= 0;
    };
    const onTouchStart = (e) => {
      sheetTouchStartY.current = e.touches[0].clientY;
      sheetDragging.current = false;
    };
    const onTouchMove = (e) => {
      if (sheetDragging.current) {
        e.preventDefault();
        return;
      }
      if (sheetTouchStartY.current === null) return;
      const deltaY = e.touches[0].clientY - sheetTouchStartY.current;
      if (deltaY > 8 && isSheetScrolledToTop.current) {
        sheetDragging.current = true;
        e.preventDefault();
        const t = e.touches[0];
        sheetDragControls.start(
          new PointerEvent("pointerdown", {
            clientX: t.clientX,
            clientY: t.clientY,
            pointerId: 1,
            pointerType: "touch",
            bubbles: true,
          }),
        );
      }
    };
    const onTouchEnd = () => {
      sheetTouchStartY.current = null;
      sheetDragging.current = false;
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [isMobile, sheetDragControls, dialogOpen]);

  useEffect(() => {
    fetchTickets();

    // Check for query params to pre-fill ticket
    const searchParams = new URLSearchParams(location.search);
    const newTicketParam = searchParams.get("newTicket");
    const titleParam = searchParams.get("title");

    if (newTicketParam === "true") {
      if (titleParam) {
        setNewTicketTitle(titleParam);
      }
      setDialogOpen(true);
    }
  }, [location.search]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const data = await getCompanyTickets();
      setTickets(data || []);
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setError("Er is een fout opgetreden bij het ophalen van uw tickets.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const newTicket = await createTicket({
        title: newTicketTitle,
        description: newTicketDescription,
      });

      setTickets([newTicket, ...tickets]);
      setSuccess("Ticket is succesvol aangemaakt!");
      setTimeout(() => setSuccess(""), 3000);
      setNewTicketTitle("");
      setNewTicketDescription("");
      setDialogOpen(false);
    } catch (err) {
      console.error("Error creating ticket:", err);
      setError("Er is een fout opgetreden bij het aanmaken van het ticket.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredTickets =
    statusFilter === "all"
      ? tickets
      : tickets.filter((ticket) => ticket.status === statusFilter);

  // Count tickets by status
  const statusCounts = {
    total: tickets.length,
    open: tickets.filter((ticket) => ticket.status === "open").length,
    inProgress: tickets.filter((ticket) => ticket.status === "in progress")
      .length,
    closed: tickets.filter((ticket) => ticket.status === "closed").length,
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div
          className={`w-8 h-8 rounded-full border-2 animate-spin ${isDark ? "border-white/[0.08] border-t-white/40" : "border-gray-200 border-t-gray-600"}`}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { label: "Totaal", value: statusCounts.total, icon: MessageSquare },
          {
            label: "In behandeling",
            value: statusCounts.inProgress,
            icon: Clock3,
            dot: isDark ? "bg-amber-400" : "bg-amber-500",
          },
          {
            label: "Opgelost",
            value: statusCounts.closed,
            icon: CheckCircle2,
            dot: isDark ? "bg-emerald-400" : "bg-emerald-500",
          },
        ].map((s) => (
          <div
            key={s.label}
            className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            <div className="flex items-center gap-1.5 mb-2">
              <s.icon
                className={`h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
              />
              <span
                className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                {s.label}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {s.dot && <div className={`h-2 w-2 rounded-full ${s.dot}`} />}
              <span
                className={`text-xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                {s.value}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1
          className={`text-[15px] font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
        >
          Tickets
        </h1>
        <button
          onClick={() => setDialogOpen(true)}
          className={`h-9 px-4 rounded-lg text-[13px] font-medium inline-flex items-center gap-2 transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
        >
          <Plus className="h-3.5 w-3.5" />
          Nieuw ticket
        </button>
      </div>

      {/* New ticket modal */}
      {dialogOpen && (
        <div
          className={`fixed inset-0 z-50 flex ${isMobile ? "items-end" : "items-center"} justify-center`}
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setDialogOpen(false)}
          />
          <motion.div
            ref={sheetRef}
            initial={isMobile ? { y: "100%" } : { scale: 0.95, opacity: 0 }}
            animate={isMobile ? { y: 0 } : { scale: 1, opacity: 1 }}
            exit={isMobile ? { y: "100%" } : { scale: 0.95, opacity: 0 }}
            transition={
              isMobile
                ? { type: "spring", damping: 35, stiffness: 400 }
                : { type: "spring", duration: 0.3 }
            }
            drag={isMobile ? "y" : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            dragListener={false}
            dragControls={sheetDragControls}
            onDragEnd={(_, info) => {
              if (info.offset.y > 80 || info.velocity.y > 500) {
                setDialogOpen(false);
              }
            }}
            style={
              isMobile
                ? {
                    y: sheetY,
                    borderTopLeftRadius: "1rem",
                    borderTopRightRadius: "1rem",
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                  }
                : undefined
            }
            className={`relative w-full ${isMobile ? "max-w-full max-h-[85dvh] flex flex-col" : "rounded-xl max-w-md mx-4"} ${isDark ? "bg-[#141414] border border-white/[0.06]" : "bg-white border border-gray-200"}`}
          >
            {isMobile && (
              <div
                onPointerDown={(e) => sheetDragControls.start(e)}
                className="flex justify-center pt-3 pb-1 shrink-0 cursor-grab active:cursor-grabbing touch-none"
                style={{ touchAction: "none" }}
              >
                <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
              </div>
            )}
            <div
              className={
                isMobile
                  ? "p-6 overflow-y-auto overscroll-contain flex-1"
                  : "p-6"
              }
              ref={isMobile ? sheetContentRef : undefined}
            >
              <div className="flex items-center justify-between mb-1">
                <h2
                  className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  Nieuw ticket aanmaken
                </h2>
                <button
                  onClick={() => setDialogOpen(false)}
                  className={`h-7 w-7 rounded-lg flex items-center justify-center ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <p
                className={`text-[12px] mb-5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Beschrijf uw verzoek of probleem en we zullen er zo snel
                mogelijk naar kijken.
              </p>

              <form onSubmit={handleCreateTicket} className="space-y-4">
                <div>
                  <label
                    className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Onderwerp
                  </label>
                  <input
                    value={newTicketTitle}
                    onChange={(e) => setNewTicketTitle(e.target.value)}
                    placeholder="Voer een onderwerp in"
                    required
                    className={`w-full h-9 px-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                  />
                </div>
                <div>
                  <label
                    className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Beschrijving
                  </label>
                  <textarea
                    value={newTicketDescription}
                    onChange={(e) => setNewTicketDescription(e.target.value)}
                    placeholder="Beschrijf uw verzoek of probleem gedetailleerd"
                    required
                    rows={5}
                    className={`w-full px-3 py-2 rounded-lg text-[13px] outline-none resize-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setDialogOpen(false)}
                    className={`h-9 px-4 rounded-lg text-[13px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                  >
                    Annuleren
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`h-9 px-4 rounded-lg text-[13px] font-medium inline-flex items-center gap-2 transition-colors disabled:opacity-40 ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
                  >
                    {isSubmitting ? (
                      <>
                        <div
                          className={`h-3.5 w-3.5 rounded-full border-2 animate-spin ${isDark ? "border-black/20 border-t-black" : "border-white/20 border-t-white"}`}
                        />
                        Verzenden...
                      </>
                    ) : (
                      <>
                        <MessageSquare className="h-3.5 w-3.5" />
                        Ticket indienen
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}

      {/* Notifications */}
      {error && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-[13px] ${isDark ? "bg-red-500/10 border border-red-500/20 text-red-400" : "bg-red-50 border border-red-200 text-red-700"}`}
        >
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-[13px] ${isDark ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400" : "bg-emerald-50 border border-emerald-200 text-emerald-700"}`}
        >
          <Check className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger
            className={`w-[180px] h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
          >
            <div className="flex items-center gap-2">
              <Filter className="h-3.5 w-3.5" />
              <SelectValue placeholder="Filter op status" />
            </div>
          </SelectTrigger>
          <SelectContent
            className={isDark ? "bg-[#1a1a1a] border-white/[0.08]" : ""}
          >
            <SelectItem value="all">Alle tickets</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in progress">In behandeling</SelectItem>
            <SelectItem value="closed">Gesloten</SelectItem>
          </SelectContent>
        </Select>
        <span
          className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-500"}`}
        >
          {filteredTickets.length} ticket
          {filteredTickets.length !== 1 ? "s" : ""} gevonden
        </span>
      </div>

      {/* Tickets list */}
      {tickets.length === 0 ? (
        <div
          className={`rounded-xl p-12 text-center ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
        >
          <div
            className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
          >
            <MessageSquare
              className={`h-6 w-6 ${isDark ? "text-gray-600" : "text-gray-400"}`}
            />
          </div>
          <h3
            className={`text-[14px] font-semibold mb-1 ${isDark ? "text-gray-300" : "text-gray-900"}`}
          >
            Nog geen tickets
          </h3>
          <p
            className={`text-[13px] mb-5 max-w-md mx-auto ${isDark ? "text-gray-600" : "text-gray-500"}`}
          >
            U heeft nog geen support tickets aangemaakt. Heeft u een vraag of
            probleem? Maak dan uw eerste ticket aan.
          </p>
          <button
            onClick={() => setDialogOpen(true)}
            className={`h-9 px-4 rounded-lg text-[13px] font-medium inline-flex items-center gap-2 transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
          >
            <Plus className="h-3.5 w-3.5" />
            Eerste ticket aanmaken
          </button>
        </div>
      ) : filteredTickets.length === 0 ? (
        <div
          className={`rounded-xl p-12 text-center ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
        >
          <div
            className={`w-10 h-10 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
          >
            <Filter
              className={`h-5 w-5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
            />
          </div>
          <p
            className={`text-[13px] mb-3 ${isDark ? "text-gray-500" : "text-gray-500"}`}
          >
            Geen tickets gevonden met de geselecteerde status.
          </p>
          <button
            onClick={() => setStatusFilter("all")}
            className={`h-8 px-3 rounded-lg text-[12px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
          >
            Toon alle tickets
          </button>
        </div>
      ) : (
        <>
          {/* Mobile compact cards */}
          <div className="sm:hidden space-y-3">
            {filteredTickets.map((ticket, i) => (
              <motion.div
                key={ticket.id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
              >
                <div className="p-3 flex items-center gap-3">
                  <div
                    className={`h-11 w-11 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                  >
                    <MessageSquare
                      className={`h-5 w-5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3
                      className={`text-[13px] font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      {ticket.title}
                    </h3>
                    <span
                      className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                    >
                      #{ticket.id} · {formatDate(ticket.createdAt)}
                    </span>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium flex-shrink-0 ${getStatusColor(ticket.status, isDark)}`}
                  >
                    {getStatusIcon(ticket.status)}
                    {ticket.status === "open" && "Open"}
                    {ticket.status === "in progress" && "Bezig"}
                    {ticket.status === "closed" && "Klaar"}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Desktop detailed cards */}
          <div className="hidden sm:block space-y-3">
            {filteredTickets.map((ticket) => (
              <div
                key={ticket.id}
                className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3
                        className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {ticket.title}
                      </h3>
                      <span
                        className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                      >
                        #{ticket.id} · {formatDate(ticket.createdAt)}
                      </span>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${getStatusColor(ticket.status, isDark)}`}
                    >
                      {getStatusIcon(ticket.status)}
                      {ticket.status === "open" && "Open"}
                      {ticket.status === "in progress" && "In behandeling"}
                      {ticket.status === "closed" && "Gesloten"}
                    </span>
                  </div>
                  <div
                    className={`border-t pt-3 mt-3 ${isDark ? "border-white/[0.04]" : "border-gray-100"}`}
                  >
                    <p
                      className={`text-[13px] whitespace-pre-wrap ${isDark ? "text-gray-400" : "text-gray-600"}`}
                    >
                      {ticket.description}
                    </p>
                  </div>
                  {ticket.updatedAt &&
                    ticket.updatedAt !== ticket.createdAt && (
                      <div
                        className={`border-t pt-2 mt-3 ${isDark ? "border-white/[0.04]" : "border-gray-100"}`}
                      >
                        <p
                          className={`text-[11px] ${isDark ? "text-gray-700" : "text-gray-400"}`}
                        >
                          Laatste update: {formatDate(ticket.updatedAt)}
                        </p>
                      </div>
                    )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default TicketsManager;
